import React, { useState, useMemo, useRef, useEffect } from "react";
import { Search, X, Package } from "lucide-react";
import { normalize } from "@/components/Brutal";

/**
 * Combobox com busca para selecionar Produto.
 * Mostra: Código · Nome · Unidade · Estoque atual.
 * Props:
 *  - items: lista de produtos
 *  - value: id selecionado
 *  - onChange: fn(id, productObj) chamada ao selecionar
 *  - placeholder, testid
 */
export default function ProductCombobox({ items = [], value, onChange, placeholder = "Buscar produto por código ou nome...", testid, required = false }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const filtered = useMemo(() => {
    const nq = normalize(query);
    const list = items.filter((p) => {
      if (!nq) return true;
      return (
        normalize(p.codigo).includes(nq) ||
        normalize(p.nome).includes(nq) ||
        normalize(p.unidade).includes(nq) ||
        normalize(p.categoria).includes(nq)
      );
    });
    return list.slice(0, 80);
  }, [items, query]);

  const selected = items.find((p) => p.id === value);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const pick = (p) => { onChange(p.id, p); setOpen(false); setQuery(""); };
  const clear = () => { onChange("", null); setQuery(""); setOpen(true); };

  return (
    <div ref={wrapRef} className="relative" data-testid={testid}>
      {selected ? (
        <div className="flex items-center justify-between gap-2 border-2 border-[#111] bg-[#F4F8F5] p-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono-data text-[10px] font-bold px-1.5 py-0.5 bg-[#111] text-white tracking-widest shrink-0">{selected.codigo}</span>
              <span className="font-medium text-sm truncate">{selected.nome}</span>
            </div>
            <div className="font-mono-data text-[10px] text-[#777] mt-0.5">
              {selected.unidade && <>Unidade · <b>{selected.unidade}</b></>}
              {selected.estoque_atual != null && <> · Estoque <b className={selected.estoque_atual <= (selected.estoque_minimo || 0) ? "text-[#E60000]" : "text-[#3D6B57]"}>{selected.estoque_atual}</b></>}
            </div>
          </div>
          <button type="button" onClick={clear} className="shrink-0 p-1.5 border-2 border-[#111] hover:bg-[#E60000] hover:text-white" data-testid={testid ? `${testid}-clear` : undefined} title="Trocar produto">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2 border-2 border-[#111] bg-white p-2 focus-within:border-[#FF4F00]">
          <Search className="w-4 h-4 text-[#555] shrink-0" />
          <input
            type="text"
            value={query}
            required={required}
            onFocus={() => setOpen(true)}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            placeholder={placeholder}
            className="flex-1 outline-none text-sm bg-transparent font-mono-data"
            data-testid={testid ? `${testid}-input` : undefined}
          />
        </div>
      )}

      {open && !selected && (
        <div className="absolute z-30 left-0 right-0 mt-1 max-h-72 overflow-y-auto border-2 border-[#111] bg-white shadow-xl" data-testid={testid ? `${testid}-list` : undefined}>
          {filtered.length === 0 ? (
            <div className="p-4 text-center text-xs font-mono-data text-[#777] uppercase tracking-widest">
              Nenhum produto para "{query}"
            </div>
          ) : (
            filtered.map((p) => {
              const low = p.estoque_atual != null && p.estoque_atual <= (p.estoque_minimo || 0);
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => pick(p)}
                  data-testid={testid ? `${testid}-opt-${p.id}` : undefined}
                  className="w-full text-left px-3 py-2 hover:bg-[#F4F8F5] border-b border-[#eee] last:border-b-0 flex items-start gap-2"
                >
                  <span className="shrink-0 w-7 h-7 flex items-center justify-center bg-[#3D6B57] text-white">
                    <Package className="w-3.5 h-3.5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap text-sm">
                      <span className="font-mono-data text-[10px] font-bold px-1.5 py-0.5 bg-[#111] text-white tracking-widest">{p.codigo}</span>
                      <span className="font-medium">{p.nome}</span>
                    </div>
                    <div className="font-mono-data text-[10px] text-[#777] mt-0.5">
                      {p.unidade && <>Un. <b>{p.unidade}</b></>}
                      {p.estoque_atual != null && <> · Estoque <b className={low ? "text-[#E60000]" : "text-[#3D6B57]"}>{p.estoque_atual}</b></>}
                      {p.valor_medio != null && p.valor_medio > 0 && <> · Vlr médio <b>R$ {Number(p.valor_medio).toFixed(2)}</b></>}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
